<script setup lang="ts">
/**
 * /actions/runs/:id — one run of an action, live: approval state, step
 * progress (pending → running → succeeded/failed/skipped) with output and
 * timings, the parameters used and "Run again".
 */
import { ArrowRight, CircleAlert, Clock, FileText, Hourglass, ListChecks, Radio, RotateCcw, SlidersHorizontal } from "@lucide/vue";
import { computed } from "vue";
import { useRoute } from "vue-router";
import { liveConnected } from "@/api/events";
import { encodeParams, isProd, useRun } from "@/api/runbooks";
import StepTimeline from "@/components/actions/StepTimeline.vue";
import TargetChip from "@/components/actions/TargetChip.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import RiskBadge from "@/components/common/RiskBadge.vue";
import SectionCard from "@/components/common/SectionCard.vue";
import StatusBadge from "@/components/common/StatusBadge.vue";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { t } from "@/copy/strings";
import { formatDateTime, formatDuration, humanize } from "@/lib/format";

definePage({ meta: { title: "Action run" } });

const route = useRoute("/actions/runs/[id]");
const id = computed(() => route.params.id);
const run = useRun(id);
const data = computed(() => run.data.value);

const running = computed(() => data.value?.state === "running");
const waiting = computed(() => data.value?.state === "awaiting_approval");
const percent = computed(() => (data.value && data.value.stepCount ? Math.round((data.value.stepsDone / data.value.stepCount) * 100) : 0));
const title = computed(() => (data.value ? t.actions.runTitle(data.value.title ?? data.value.runbook, data.value.target) : ""));
const again = computed(() =>
  data.value ? { path: `/actions/${data.value.runbook}`, query: { ...(data.value.target ? { target: data.value.target } : {}), ...(encodeParams(data.value.params) ? { params: encodeParams(data.value.params) } : {}) }, hash: "#run" } : "/actions",
);
const paramEntries = computed(() => Object.entries(data.value?.params ?? {}));
/** Live duration while running (RelativeTime-style clock is not needed: the page refetches every 2 s). */
const elapsed = computed(() => {
  const d = data.value;
  if (!d) return null;
  if (d.durationMs !== null) return d.durationMs;
  if (d.startedAt) return Date.now() - Date.parse(d.startedAt);
  return null;
});
</script>

<template>
  <div>
    <ListSkeleton v-if="run.isPending.value" :rows="5" />
    <ErrorState v-else-if="run.error.value" :error="run.error.value" what="Run" @retry="run.refetch()" />
    <template v-else-if="data">
      <PageHeader
        :title="title"
        :crumbs="[{ label: t.actions.title, to: '/actions' }, { label: data.title ?? data.runbook, to: `/actions/${data.runbook}` }, { label: `Run ${data.id.slice(0, 8)}` }]"
      >
        <template #badges>
          <StatusBadge :status="data.state" :label="t.actions.state[data.state]" />
          <RiskBadge v-if="data.risk !== 'low'" :risk="data.risk" />
          <span v-if="running && liveConnected" class="inline-flex items-center gap-1 text-xs font-medium text-info"><Radio class="size-3.5 animate-pulse" aria-hidden="true" /> {{ t.actions.live }}</span>
        </template>
        <template #meta>
          <dl class="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
            <div class="flex gap-1.5"><dt class="text-muted-foreground">{{ t.actions.requester }}</dt><dd class="font-medium">{{ data.requester ?? "unknown" }}</dd></div>
            <div v-if="data.approver && data.approver !== data.requester" class="flex gap-1.5"><dt class="text-muted-foreground">{{ t.actions.approver }}</dt><dd class="font-medium">{{ data.approver }}</dd></div>
            <div class="flex gap-1.5"><dt class="text-muted-foreground">{{ t.actions.started }}</dt><dd><RelativeTime :value="data.startedAt ?? data.createdAt" /></dd></div>
            <div v-if="elapsed !== null" class="flex gap-1.5"><dt class="text-muted-foreground">{{ t.actions.duration }}</dt><dd class="tabular-nums">{{ formatDuration(elapsed) }}</dd></div>
            <div v-if="data.confirmedVia" class="flex gap-1.5"><dt class="text-muted-foreground">{{ t.actions.confirmedVia }}</dt><dd>{{ humanize(data.confirmedVia) }}</dd></div>
          </dl>
        </template>
        <template #actions>
          <Button as-child variant="outline"><RouterLink :to="again"><RotateCcw /> {{ t.actions.runAgain }}</RouterLink></Button>
        </template>
      </PageHeader>

      <!-- State banners -->
      <div v-if="waiting && data.approvalPath" role="status" class="mb-6 flex flex-col gap-3 rounded-xl border border-warning/50 bg-warning-soft/60 p-4 sm:flex-row sm:items-center">
        <Hourglass class="size-5 shrink-0 text-warning" aria-hidden="true" />
        <div class="min-w-0 flex-1">
          <p class="font-semibold">{{ t.actions.awaitingApproval }}</p>
          <p class="text-sm text-muted-foreground">{{ t.actions.awaitingApprovalHint }}</p>
          <p v-if="data.expiresAt" class="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><Clock class="size-3" aria-hidden="true" /> Expires <RelativeTime :value="data.expiresAt" /></p>
        </div>
        <Button as-child><RouterLink :to="data.approvalPath">{{ t.actions.reviewApprove }} <ArrowRight /></RouterLink></Button>
      </div>
      <p v-else-if="data.state === 'declined' || data.state === 'expired'" class="surface-inset mb-6 p-4 text-sm text-muted-foreground">
        {{ data.state === "declined" ? t.actions.declinedHint : t.actions.expiredHint }}
      </p>
      <p v-if="data.error" role="alert" class="mb-6 flex gap-2 rounded-xl border border-destructive/30 bg-destructive-soft p-4 text-sm text-destructive">
        <CircleAlert class="mt-0.5 size-4 shrink-0" aria-hidden="true" /> {{ data.error }}
      </p>

      <div class="grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <SectionCard :title="t.actions.steps" :icon="ListChecks" class="min-w-0">
          <template #badge>
            <span class="text-xs text-muted-foreground tabular-nums">{{ t.actions.stepsDone(data.stepsDone, data.stepCount) }}</span>
          </template>
          <Progress
            v-if="running || (data.stepsDone > 0 && data.stepsDone < data.stepCount)"
            :model-value="percent"
            class="mb-5 h-1.5"
            :aria-label="t.actions.stepsDone(data.stepsDone, data.stepCount)"
          />
          <StepTimeline :steps="data.steps" />
        </SectionCard>

        <aside class="flex min-w-0 flex-col gap-6">
          <SectionCard :title="t.actions.target" :icon="SlidersHorizontal">
            <div class="flex flex-col gap-3 text-sm">
              <div v-if="data.target" class="flex items-center gap-2">
                <TargetChip :target="data.target" :to="`/actions?target=${encodeURIComponent(data.target)}`" />
                <span v-if="isProd(data.target)" class="text-xs font-medium text-destructive">{{ t.actions.production }}</span>
              </div>
              <p v-if="data.datasource" class="font-mono text-xs text-muted-foreground">{{ data.datasource }}</p>
              <div>
                <p class="mb-1.5 text-xs font-medium text-muted-foreground">{{ t.actions.paramsUsed }}</p>
                <p v-if="paramEntries.length === 0" class="text-muted-foreground">{{ t.actions.noParameters }}</p>
                <dl v-else class="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1">
                  <template v-for="[k, v] in paramEntries" :key="k">
                    <dt class="text-muted-foreground">{{ k }}</dt>
                    <dd class="font-mono text-xs break-all">{{ typeof v === "object" ? JSON.stringify(v) : String(v) }}</dd>
                  </template>
                </dl>
              </div>
            </div>
          </SectionCard>
          <SectionCard title="Records" :icon="FileText">
            <ul class="flex flex-col gap-1 text-sm">
              <li>
                <RouterLink :to="`/approvals/${data.id}`" class="-mx-2 flex items-center justify-between gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-state-hover active:bg-state-press">
                  {{ t.actions.operation }} <span class="font-mono text-xs text-muted-foreground">{{ data.id.slice(0, 8) }}</span>
                </RouterLink>
              </li>
              <li v-if="data.jobId">
                <RouterLink :to="`/jobs/${data.jobId}`" class="-mx-2 flex items-center justify-between gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-state-hover active:bg-state-press">
                  {{ t.actions.job }} <span class="font-mono text-xs text-muted-foreground">{{ data.jobId.slice(0, 8) }}</span>
                </RouterLink>
              </li>
            </ul>
            <p class="mt-2 text-xs text-muted-foreground" :title="formatDateTime(data.createdAt)">Requested <RelativeTime :value="data.createdAt" /></p>
          </SectionCard>
        </aside>
      </div>
    </template>
  </div>
</template>
